import { useCallback, useEffect, useState } from 'react'
import { Key, Plus, X, Trash2, RotateCw, RefreshCw } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Cred {
  id: number
  label: string
  kind: string
  owner: string
  expires_at: string
  rotation_url: string | null
  notes: string
  last_rotated_at: string | null
  days_left: number | null
  urgency: string
}

const URG_TONE: Record<string, string> = {
  expired:  'bg-rose/30 text-rose font-bold',
  critical: 'bg-rose/15 text-rose-light',
  warning:  'bg-amber/15 text-amber-light',
  ok:       'bg-emerald/10 text-emerald',
}

const KINDS = ['api_key', 'cert', 'secret', 'oauth_token', 'password', 'SSH_key', 'signing_key']

export default function CredentialExpiryPage() {
  const [creds, setCreds] = useState<Cred[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState({ label: '', kind: 'api_key', owner: '', expires_at: '', rotation_url: '', notes: '' })
  const [days, setDays] = useState(180)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api.get('/api/credentials', { params: { days_ahead: days } })
      setCreds(r.data?.credentials || [])
    } finally { setLoading(false) }
  }, [days])

  useEffect(() => { fetchAll() }, [fetchAll])

  const create = async () => {
    if (!form.label || !form.expires_at) return
    try {
      await api.post('/api/credentials', { ...form, expires_at: new Date(form.expires_at).toISOString() })
      setShowForm(false)
      setForm({ label: '', kind: 'api_key', owner: '', expires_at: '', rotation_url: '', notes: '' })
      fetchAll()
    } catch {}
  }

  const rotate = async (id: number) => {
    const newDate = prompt('New expiry date (YYYY-MM-DD)')
    if (!newDate) return
    try {
      await api.post(`/api/credentials/${id}/rotate`, null, { params: { new_expires_at: new Date(newDate + 'T00:00:00Z').toISOString() } })
      fetchAll()
    } catch {}
  }

  const remove = async (id: number) => {
    if (!confirm('Delete?')) return
    await api.delete(`/api/credentials/${id}`)
    fetchAll()
  }

  const summary = {
    expired:  creds.filter(c => c.urgency === 'expired').length,
    critical: creds.filter(c => c.urgency === 'critical').length,
    warning:  creds.filter(c => c.urgency === 'warning').length,
    ok:       creds.filter(c => c.urgency === 'ok').length,
  }

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Key size={24} className="text-amber-light" /> Credential Expiry
          </h1>
          <p className="text-sm text-text-secondary mt-1">
            Track API keys, certs, signing keys nearing expiry. Rotate before they bite.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={days} onChange={e => setDays(parseInt(e.target.value, 10))} className="input py-1.5 text-xs">
            <option value={30}>30d</option><option value={90}>90d</option><option value={180}>180d</option><option value={365}>365d</option>
          </select>
          <button type="button" onClick={fetchAll} className="btn-ghost text-xs"><RefreshCw size={11} className={loading ? 'animate-spin' : ''} /></button>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary text-xs"><Plus size={12} /> Add credential</button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Expired"  value={summary.expired}  tone="rose"    pulse={summary.expired > 0} />
        <Tile label="Critical (≤7d)"  value={summary.critical} tone="rose" pulse={summary.critical > 0} />
        <Tile label="Warning (≤30d)" value={summary.warning} tone="amber" />
        <Tile label="OK"       value={summary.ok}       tone="emerald" />
      </div>

      {showForm && (
        <div className="card p-4 border border-blue/40">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">Add credential</h3>
            <button type="button" onClick={() => setShowForm(false)} className="text-text-muted hover:text-text-primary"><X size={14} /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
            <input value={form.label} onChange={e => setForm({...form, label: e.target.value})} placeholder='Label (e.g. "Stripe API key")' className="input py-1.5 md:col-span-2" />
            <select value={form.kind} onChange={e => setForm({...form, kind: e.target.value})} className="input py-1.5">
              {KINDS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            <input value={form.owner} onChange={e => setForm({...form, owner: e.target.value})} placeholder="Owner" className="input py-1.5" />
            <label className="space-y-0.5"><span className="text-text-muted">Expires at</span>
              <input type="datetime-local" value={form.expires_at} onChange={e => setForm({...form, expires_at: e.target.value})} className="input w-full py-1.5" />
            </label>
            <input value={form.rotation_url} onChange={e => setForm({...form, rotation_url: e.target.value})} placeholder="Rotation URL (where to rotate it)" className="input py-1.5" />
            <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} placeholder="Notes" className="input py-1.5 md:col-span-2" />
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button type="button" onClick={() => setShowForm(false)} className="btn-ghost text-xs">Cancel</button>
            <button type="button" onClick={create} className="btn-primary text-xs">Save</button>
          </div>
        </div>
      )}

      <div className="card p-3 overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider text-text-muted border-b border-border">
              <th className="text-left py-2 px-2">Label</th>
              <th className="text-left py-2 px-2">Kind</th>
              <th className="text-left py-2 px-2">Owner</th>
              <th className="text-right py-2 px-2">Days left</th>
              <th className="text-left py-2 px-2">Expires</th>
              <th className="text-right py-2 px-2"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {creds.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-text-muted text-[11px] italic">No credentials tracked yet — add one above.</td></tr>
            )}
            {creds.map(c => (
              <tr key={c.id} className="border-b border-border/50 hover:bg-surface-hover">
                <td className="py-1.5 px-2">
                  <div className="text-text-primary font-medium">{c.label}</div>
                  {c.rotation_url && <a href={c.rotation_url} target="_blank" rel="noreferrer" className="text-[9px] text-blue-light hover:underline">rotate →</a>}
                </td>
                <td className="py-1.5 px-2 font-mono text-[10px] text-text-muted">{c.kind}</td>
                <td className="py-1.5 px-2 text-text-secondary">{c.owner || '—'}</td>
                <td className="py-1.5 px-2 text-right">
                  <span className={clsx('px-1.5 py-0.5 rounded font-mono', URG_TONE[c.urgency])}>
                    {c.days_left == null ? '?' : c.days_left < 0 ? `${Math.abs(c.days_left)}d ago` : `${c.days_left}d`}
                  </span>
                </td>
                <td className="py-1.5 px-2 text-[10px] text-text-muted">{new Date(c.expires_at).toLocaleDateString()}</td>
                <td className="py-1.5 px-2 text-right space-x-1">
                  <button type="button" onClick={() => rotate(c.id)} className="text-blue-light hover:text-blue" title="Rotate"><RotateCw size={11} /></button>
                  <button type="button" onClick={() => remove(c.id)} className="text-text-muted hover:text-rose" title="Delete"><Trash2 size={11} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Tile({ label, value, tone, pulse }: { label: string; value: number; tone: string; pulse?: boolean }) {
  const t = tone === 'rose' ? 'text-rose' : tone === 'amber' ? 'text-amber-light' : tone === 'emerald' ? 'text-emerald' : 'text-text-primary'
  return (
    <div className="card p-3">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-bold mt-1 font-mono', t, pulse && 'animate-pulse')}>{value}</div>
    </div>
  )
}
